import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, RefreshCw, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAdminContent } from "@/context/AdminContentContext";
import { Field, Panel, TextArea, TextInput } from "@/components/admin/Field";
import { Switch } from "@/components/ui/switch";

const clean = (s) => s.replace(/[\p{Extended_Pictographic}\p{Emoji_Modifier}\u2640-\u2642\u200d\ufe0f┃|]/gu, "").trim();
const dot = (r) => <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: r?.color ? `#${r.color.toString(16).padStart(6, "0")}` : "#666" }} aria-hidden="true" />;

function IntentWarning() {
  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="staff-intent-warning">
      <p className="font-semibold">O bot ainda não consegue ler a lista de membros do Discord.</p>
      <p className="mt-1 text-amber-200/80">Ativa o <strong>Server Members Intent</strong> no Discord Developer Portal (Bot → Privileged Gateway Intents) e carrega em "Atualizar".</p>
    </div>
  );
}

function AutoPreview({ roles, autoIds, staff, update }) {
  const byId = Object.fromEntries(roles.map((r) => [r.id, r]));
  return (
    <div className="space-y-4" data-testid="staff-auto-preview">
      <div>
        <p className="mb-2 text-sm font-medium text-eden-text">Cargos detetados automaticamente ({autoIds.length})</p>
        {autoIds.length === 0 ? <p className="text-sm text-eden-muted" data-testid="staff-auto-empty">Nenhum cargo de staff detetado. Escolhe abaixo o cargo mais baixo da tua staff.</p> : (
          <ul className="flex flex-wrap gap-2">
            {autoIds.map((id) => <li key={id} className="inline-flex items-center gap-2 rounded-full border border-eden-border bg-eden-bg px-3 py-1 text-sm text-eden-text" data-testid={`staff-auto-${id}`}>{dot(byId[id])}{clean(byId[id]?.name || id)}</li>)}
          </ul>
        )}
      </div>
      <Field label="Cargo mais baixo considerado staff" id="staff-min-role" hint="Todos os cargos acima deste (na hierarquia do Discord) contam como staff. Deixa em automático para detetar pelo nome (Staff, Helper, Estagiário…).">
        <select id="staff-min-role" value={staff.min_role_id || ""} onChange={(e) => update("staff", { min_role_id: e.target.value })} className="input-eden" data-testid="staff-min-role-select">
          <option value="">Automático (pelo nome dos cargos)</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{clean(r.name) || r.name}</option>)}
        </select>
      </Field>
      <p className="text-xs text-eden-muted">Cargos de utilidade (TAGS, Config, VIP, Booster, separadores) e cargos sem membros são ignorados. Quem tiver vários cargos aparece só no mais alto.</p>
    </div>
  );
}

function ManualPicker({ roles, selected, setIds }) {
  const byId = Object.fromEntries(roles.map((r) => [r.id, r]));
  const toggle = (id) => setIds(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= selected.length) return; const n = [...selected]; [n[i], n[j]] = [n[j], n[i]]; setIds(n); };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <ul className="max-h-80 space-y-1 overflow-y-auto rounded-md border border-eden-border bg-eden-bg p-2" data-testid="staff-roles-list">
        {roles.map((r) => (
          <li key={r.id}>
            <label className="flex cursor-pointer items-center gap-3 rounded-sm px-2 py-1.5 text-sm text-eden-text hover:bg-eden-surface">
              <input type="checkbox" className="h-4 w-4 accent-eden-orange" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} data-testid={`staff-role-toggle-${r.id}`} />
              {dot(r)}{clean(r.name) || r.name}
            </label>
          </li>
        ))}
      </ul>
      <ol className="space-y-1" data-testid="staff-selected-list">
        {selected.length === 0 && <p className="text-sm text-eden-muted" data-testid="staff-selected-empty">Nenhum cargo escolhido.</p>}
        {selected.map((id, i) => (
          <li key={id} className="flex items-center gap-2 rounded-md border border-eden-border bg-eden-bg px-3 py-2 text-sm text-eden-text" data-testid={`staff-selected-${id}`}>
            <span className="font-mono text-xs text-eden-muted">{i + 1}</span>
            <span className="flex-1">{byId[id] ? clean(byId[id].name) || byId[id].name : id}</span>
            <button type="button" className="btn-ghost h-7 px-2" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Subir" data-testid={`staff-up-${id}`}><ArrowUp size={14} /></button>
            <button type="button" className="btn-ghost h-7 px-2" onClick={() => move(i, 1)} disabled={i === selected.length - 1} aria-label="Descer" data-testid={`staff-down-${id}`}><ArrowDown size={14} /></button>
            <button type="button" className="btn-ghost h-7 px-2 text-red-400" onClick={() => toggle(id)} aria-label="Remover" data-testid={`staff-remove-${id}`}><X size={14} /></button>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function StaffEditor() {
  const { draft, update } = useAdminContent();
  const staff = draft.staff || { title: "", text: "", role_ids: [], auto: true };
  const auto = staff.auto !== false;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/discord/roles").then(({ data: d }) => setData(d)).catch(() => setData({ roles: [], error: "unavailable", auto_role_ids: [] })).finally(() => setLoading(false));
  };
  useEffect(load, [staff.min_role_id]);

  return (
    <Panel title="Equipa de staff (cargos do Discord)" description="Os membros da staff e os seus cargos são lidos automaticamente do Discord. Basta dar ou tirar o cargo a alguém no Discord e o site atualiza sozinho.">
      <div className="flex items-center justify-between gap-4 rounded-md border border-eden-border bg-eden-bg p-4" data-testid="staff-visible-row">
        <div>
          <p className="text-sm font-medium text-eden-text">Mostrar secção "Equipa" no site</p>
          <p className="text-xs text-eden-muted">{staff.hidden ? "Desligado: a equipa não aparece no site público." : "Ligado: a equipa aparece no Início e em Informações."}</p>
        </div>
        <Switch checked={!staff.hidden} onCheckedChange={(v) => update("staff", { hidden: !v })} aria-label="Mostrar equipa no site" data-testid="staff-visible-switch" />
      </div>
      <Field label="Título da secção" id="staff-title"><TextInput id="staff-title" value={staff.title} onChange={(v) => update("staff", { title: v })} maxLength={120} /></Field>
      <Field label="Texto de apoio" id="staff-text"><TextArea id="staff-text" value={staff.text} onChange={(v) => update("staff", { text: v })} rows={2} maxLength={400} /></Field>
      <div className="space-y-4 rounded-md border border-eden-border bg-eden-bg p-4" data-testid="staff-apply-editor">
        <label className="flex items-center gap-3 text-sm text-eden-text" htmlFor="staff-apply-enabled">
          <input id="staff-apply-enabled" type="checkbox" className="h-4 w-4 accent-eden-orange" checked={staff.apply_enabled !== false} onChange={(e) => update("staff", { apply_enabled: e.target.checked })} data-testid="staff-apply-enabled" />
          Mostrar botão "Junta-te à equipa" (candidaturas)
        </label>
        <Field label="Como funciona o botão" id="staff-apply-mode">
          <select id="staff-apply-mode" value={staff.apply_mode || "form"} onChange={(e) => update("staff", { apply_mode: e.target.value })} className="input-eden" data-testid="staff-apply-mode">
            <option value="form">Formulário no site (respostas chegam a Gestão › Candidaturas)</option>
            <option value="link">Abrir ligação externa (canal do Discord)</option>
          </select>
        </Field>
        {(staff.apply_mode || "form") === "form" && (
          <>
            <label className="flex items-center gap-3 text-sm text-eden-text" htmlFor="staff-apply-open">
              <input id="staff-apply-open" type="checkbox" className="h-4 w-4 accent-eden-orange" checked={staff.apply_open !== false} onChange={(e) => update("staff", { apply_open: e.target.checked })} data-testid="staff-apply-open" />
              Candidaturas abertas (desliga para fechar o formulário sem esconder o botão)
            </label>
            <Field label="Mensagem quando fechadas" id="staff-apply-closed"><TextInput id="staff-apply-closed" value={staff.apply_closed_text || ""} onChange={(v) => update("staff", { apply_closed_text: v })} maxLength={200} /></Field>
          </>
        )}
        {(staff.apply_mode || "form") === "link" && (
          <Field label="Ligação das candidaturas" id="staff-apply-url" hint="Cola a ligação do canal de candidaturas do Discord. Vazio = convite geral do Discord."><TextInput id="staff-apply-url" value={staff.apply_url || ""} onChange={(v) => update("staff", { apply_url: v })} maxLength={400} placeholder="https://discord.com/channels/…" /></Field>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Texto do botão" id="staff-apply-label"><TextInput id="staff-apply-label" value={staff.apply_label || ""} onChange={(v) => update("staff", { apply_label: v })} maxLength={40} placeholder="Junta-te à equipa" /></Field>
          <Field label="Frase de apoio" id="staff-apply-text"><TextInput id="staff-apply-text" value={staff.apply_text || ""} onChange={(v) => update("staff", { apply_text: v })} maxLength={200} /></Field>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 rounded-md border border-eden-border bg-eden-bg p-4">
        <div>
          <p className="text-sm font-medium text-eden-text">Detetar cargos de staff automaticamente</p>
          <p className="text-xs text-eden-muted">{auto ? "Ligado: novos cargos de staff criados no Discord aparecem sozinhos." : "Desligado: escolhes manualmente os cargos a mostrar."}</p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={load} disabled={loading} className="btn-ghost h-8 text-xs" data-testid="staff-roles-refresh"><RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Atualizar</button>
          <Switch checked={auto} onCheckedChange={(v) => update("staff", { auto: v })} aria-label="Detetar automaticamente" data-testid="staff-auto-switch" />
        </div>
      </div>
      {data?.error === "members_intent" && <IntentWarning />}
      {data?.error === "unavailable" && <p className="text-sm text-red-400" data-testid="staff-roles-error">Não foi possível ler os cargos do Discord. Verifica o token do bot e o ID do servidor.</p>}
      {!data && <p className="text-sm text-eden-muted">A carregar cargos do Discord…</p>}
      {data && auto && <AutoPreview roles={data.roles} autoIds={data.auto_role_ids || []} staff={staff} update={update} />}
      {data && !auto && <ManualPicker roles={data.roles} selected={staff.role_ids || []} setIds={(ids) => update("staff", { role_ids: ids })} />}
    </Panel>
  );
}
